import { RecentSearchEntry } from "@nova/schemas"
import { useCallback, useRef } from "react"
import { locationsPlugin } from "./plugins/locationsPlugin"
import { membersPlugin } from "./plugins/membersPlugin"
import { projectsPlugin } from "./plugins/projectsPlugin"
import {
    ActivatableSearchPlugin,
    AnySearchPlugin,
    RecentSearchResultEntry,
    SearchContext,
    SearchFn,
    SearchRequestContext,
    SearchResultEntry,
} from "./types"

const registeredPlugins = [projectsPlugin, membersPlugin, locationsPlugin] as const

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildPluginMap<T extends readonly AnySearchPlugin<any, string>[]>(
    plugins: T
): { [P in T[number] as P["name"]]: P } {
    const map = {} as { [P in T[number] as P["name"]]: P }
    for (const plugin of plugins) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(map as Record<string, AnySearchPlugin<any, string>>)[plugin.name] = plugin
    }
    return map
}

export const searchPlugins = buildPluginMap(registeredPlugins)

export type SearchPluginName = keyof typeof searchPlugins

export type ActivatablePluginName = Extract<
    (typeof registeredPlugins)[number],
    { FullPanelHeaderComponent: unknown; FullPanelResultComponent: unknown }
>["name"]

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SearchFnsByPlugin = Record<SearchPluginName, SearchFn<any>>
function useSearchFns(): SearchFnsByPlugin {
    const fns = registeredPlugins.reduce((acc, cur) => {
        acc[cur.name] = cur.useSearch()
        return acc
    }, {} as SearchFnsByPlugin)
    const stableFns = useRef(fns)
    const changed = registeredPlugins.some(
        (plugin) => stableFns.current[plugin.name] !== fns[plugin.name]
    )
    if (changed) stableFns.current = fns
    return stableFns.current
}

export function useRunSearch() {
    const searchFns = useSearchFns()
    return useCallback(
        async (query: string, ctx: SearchRequestContext): Promise<SearchResultEntry[]> => {
            const previewCtx: SearchContext = { ...ctx, active: false }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const plugins = Object.values(searchPlugins) as AnySearchPlugin<any, string>[]
            const settled = await Promise.allSettled(
                plugins.map(async (plugin): Promise<SearchResultEntry[]> => {
                    const data = await searchFns[plugin.name as SearchPluginName](query, previewCtx)
                    return data.map((item) => ({ plugin, data: item }))
                })
            )
            settled.forEach((result, i) => {
                if (result.status === "rejected") {
                    console.error(`[search] plugin "${plugins[i].name}" failed`, result.reason)
                }
            })
            return settled
                .filter(
                    (result): result is PromiseFulfilledResult<SearchResultEntry[]> =>
                        result.status === "fulfilled"
                )
                .flatMap((result) => result.value)
        },
        [searchFns]
    )
}

export function useRunScopedSearch() {
    const searchFns = useSearchFns()
    return useCallback(
        async (
            pluginName: ActivatablePluginName,
            query: string,
            ctx: SearchRequestContext
        ): Promise<SearchResultEntry[]> => {
            const panelCtx: SearchContext = { ...ctx, active: true }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const plugin = searchPlugins[pluginName] as AnySearchPlugin<any, string>
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const searchFn = searchFns[pluginName as SearchPluginName] as SearchFn<any>
            const data = await searchFn(query, panelCtx)
            return data.map((item) => ({ plugin, data: item }))
        },
        [searchFns]
    )
}

// protects against stale recent searches after breaking changes to plugin data schemas
export function hydrateRecentSearches(entries: RecentSearchEntry[]): RecentSearchResultEntry[] {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pluginsByName = searchPlugins as Record<string, AnySearchPlugin<any, string> | undefined>
    return entries.reduce<RecentSearchResultEntry[]>((acc, entry) => {
        const plugin = pluginsByName[entry.pluginName]
        if (!plugin) return acc
        const parsed = plugin.dataSchema.safeParse(entry.data)
        if (!parsed.success) return acc
        acc.push({ plugin, data: parsed.data, timestamp: entry.timestamp })
        return acc
    }, [])
}

export function filterRecentSearches(
    entries: RecentSearchResultEntry[],
    query: string,
    ctx: SearchRequestContext
): RecentSearchResultEntry[] {
    if (!query) return entries
    const q = query.toLowerCase()
    return entries.filter((entry) =>
        entry.plugin.getSearchableText(entry.data, ctx).toLowerCase().includes(q)
    )
}

export function getActivatablePlugin(name: ActivatablePluginName) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return searchPlugins[name] as ActivatableSearchPlugin<any, string>
}

export * from "./types"
